/** C20 订阅产品：类型与本地 mock。路径对齐文档 §8.2，接入真实接口时只改本文件。 */

export const ProductStatus = {
  Draft: 'draft',
  Scheduled: 'scheduled',
  Published: 'published',
  Unlisted: 'unlisted',
} as const
export type ProductStatus = (typeof ProductStatus)[keyof typeof ProductStatus]

export const BillingType = {
  Free: 'free',
  Workspace: 'workspace',
  Seat: 'seat',
  Usage: 'usage',
  Module: 'module',
} as const
export type BillingType = (typeof BillingType)[keyof typeof BillingType]

export const EffectiveMode = {
  AfterExpiry: 'after_expiry',
  Immediate: 'immediate',
  Scheduled: 'scheduled',
} as const
export type EffectiveMode = (typeof EffectiveMode)[keyof typeof EffectiveMode]

export const PeriodUnit = {
  Day: 'day',
  Month: 'month',
  Year: 'year',
} as const
export type PeriodUnit = (typeof PeriodUnit)[keyof typeof PeriodUnit]

export const ProductApiCode = {
  NotFound: 'NOT_FOUND',
  Limit: 'PRODUCT_LIMIT',
  NotDraft: 'NOT_DRAFT',
} as const
export type ProductApiCode = (typeof ProductApiCode)[keyof typeof ProductApiCode]

export const BILLING_TYPES = [
  BillingType.Free,
  BillingType.Workspace,
  BillingType.Seat,
  BillingType.Usage,
  BillingType.Module,
] as const

export const PERIOD_UNITS = [PeriodUnit.Day, PeriodUnit.Month, PeriodUnit.Year] as const

export const PRODUCT_STATUSES = [
  ProductStatus.Published,
  ProductStatus.Scheduled,
  ProductStatus.Draft,
  ProductStatus.Unlisted,
] as const

export type I18nText = {
  en: string
  'zh-CN'?: string
  'zh-TW'?: string
  vi?: string
}

export type ProductPeriod = {
  unit: PeriodUnit
  length: number
}

export type ProductPlan = {
  id: string
  price: number
}

export type ProductTier = {
  id: string
  name: I18nText
  description?: I18nText
  price: number
  expire_days: number
  sort_order: number
}

export type ProductModule = {
  id: string
  name: I18nText
  description?: I18nText
  price: number
  sort_order: number
}

export type SubscriptionProduct = {
  id: string
  app_id: string
  status: ProductStatus
  billing_type: BillingType
  effective_mode: EffectiveMode
  effective_at: string | null
  name: I18nText
  description: I18nText
  plans?: ProductPlan[]
  tiers?: ProductTier[]
  modules?: ProductModule[]
  periods?: ProductPeriod[]
  autorenewal_lead_days: number
  list_at: string | null
  unlist_at: string | null
  created_at: string
  updated_at: string
}

export const PRODUCT_LIMIT = 10

export class ProductApiError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.code = code
  }
}

function nowIso() {
  return new Date().toISOString()
}

function clone<T>(value: T): T {
  return structuredClone(value)
}

let seq = 10
function nextId(prefix: string) {
  seq += 1
  return `${prefix}_${seq}`
}

const store: SubscriptionProduct[] = [
  {
    id: 'prod_1',
    app_id: '',
    status: ProductStatus.Published,
    billing_type: BillingType.Free,
    effective_mode: EffectiveMode.AfterExpiry,
    effective_at: null,
    name: { en: 'Basic', 'zh-CN': '基础版' },
    description: { en: 'Free trial', 'zh-CN': '免费体验' },
    autorenewal_lead_days: 2,
    list_at: '2026-08-01T00:00:00.000Z',
    unlist_at: null,
    created_at: '2026-07-20T02:00:00.000Z',
    updated_at: '2026-09-01T04:00:00.000Z',
  },
  {
    id: 'prod_2',
    app_id: '',
    status: ProductStatus.Published,
    billing_type: BillingType.Usage,
    effective_mode: EffectiveMode.Immediate,
    effective_at: null,
    name: { en: 'AI Assistant', 'zh-CN': 'AI 助手' },
    description: { en: 'Usage-based', 'zh-CN': '按用量计费' },
    tiers: [
      {
        id: 'tier_1',
        name: { en: 'Standard Pack', 'zh-CN': '标准包' },
        description: { en: '' },
        price: 36,
        expire_days: 30,
        sort_order: 0,
      },
    ],
    autorenewal_lead_days: 2,
    list_at: '2026-08-15T00:00:00.000Z',
    unlist_at: '2026-12-31T23:59:00.000Z',
    created_at: '2026-08-10T02:00:00.000Z',
    updated_at: '2026-09-12T08:00:00.000Z',
  },
  {
    id: 'prod_3',
    app_id: '',
    status: ProductStatus.Scheduled,
    billing_type: BillingType.Workspace,
    effective_mode: EffectiveMode.AfterExpiry,
    effective_at: null,
    name: { en: 'Enterprise', 'zh-CN': '企业版' },
    description: { en: 'Workspace billing', 'zh-CN': '按工作空间' },
    plans: [{ id: 'plan_3', price: 10 }],
    periods: [
      { unit: PeriodUnit.Month, length: 1 },
      { unit: PeriodUnit.Year, length: 1 },
    ],
    autorenewal_lead_days: 2,
    list_at: '2026-10-01T09:00:00.000Z',
    unlist_at: null,
    created_at: '2026-09-01T02:00:00.000Z',
    updated_at: '2026-09-20T03:00:00.000Z',
  },
  {
    id: 'prod_4',
    app_id: '',
    status: ProductStatus.Draft,
    billing_type: BillingType.Seat,
    effective_mode: EffectiveMode.AfterExpiry,
    effective_at: null,
    name: { en: 'Pro', 'zh-CN': '专业版' },
    description: { en: 'Seat billing', 'zh-CN': '按席位计费' },
    plans: [{ id: 'plan_4', price: 20 }],
    periods: [
      { unit: PeriodUnit.Month, length: 1 },
      { unit: PeriodUnit.Year, length: 1 },
    ],
    autorenewal_lead_days: 2,
    list_at: null,
    unlist_at: null,
    created_at: '2026-09-18T02:00:00.000Z',
    updated_at: '2026-09-22T06:00:00.000Z',
  },
  {
    id: 'prod_5',
    app_id: '',
    status: ProductStatus.Unlisted,
    billing_type: BillingType.Module,
    effective_mode: EffectiveMode.Immediate,
    effective_at: null,
    name: { en: 'Productivity Suite', 'zh-CN': '效率套件' },
    description: { en: 'Module billing', 'zh-CN': '按功能模块' },
    modules: [
      {
        id: 'mod_1',
        name: { en: 'Advanced Reports', 'zh-CN': '高级报表' },
        price: 30,
        sort_order: 0,
      },
      {
        id: 'mod_2',
        name: { en: 'Audit Log', 'zh-CN': '审计日志' },
        price: 50,
        sort_order: 1,
      },
    ],
    periods: [{ unit: PeriodUnit.Month, length: 1 }],
    autorenewal_lead_days: 2,
    list_at: '2026-06-01T00:00:00.000Z',
    unlist_at: null,
    created_at: '2026-05-20T02:00:00.000Z',
    updated_at: '2026-08-01T04:00:00.000Z',
  },
]

function ofApp(appId: string) {
  return store.filter((p) => !p.app_id || p.app_id === appId)
}

function find(appId: string, id: string) {
  const item = store.find((p) => p.id === id && (!p.app_id || p.app_id === appId))
  if (!item) throw new ProductApiError(ProductApiCode.NotFound, ProductApiCode.NotFound)
  return item
}

function touch(item: SubscriptionProduct) {
  item.updated_at = nowIso()
}

function wait<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(clone(value)), 180)
  })
}

export async function listProducts(appId: string) {
  return wait(ofApp(appId).map((p) => ({ ...p, app_id: appId })))
}

export async function getProduct(appId: string, id: string) {
  return wait({ ...find(appId, id), app_id: appId })
}

export async function createProduct(
  appId: string,
  body: Partial<SubscriptionProduct>,
) {
  if (ofApp(appId).length >= PRODUCT_LIMIT) {
    throw new ProductApiError(ProductApiCode.Limit, ProductApiCode.Limit)
  }
  const created: SubscriptionProduct = {
    id: nextId('prod'),
    app_id: appId,
    status: ProductStatus.Draft,
    billing_type: body.billing_type ?? BillingType.Free,
    effective_mode: body.effective_mode ?? EffectiveMode.AfterExpiry,
    effective_at: body.effective_at ?? null,
    name: body.name ?? { en: '' },
    description: body.description ?? { en: '' },
    plans: body.plans,
    tiers: body.tiers,
    modules: body.modules,
    periods: body.periods,
    autorenewal_lead_days: 2,
    list_at: body.list_at ?? null,
    unlist_at: body.unlist_at ?? null,
    created_at: nowIso(),
    updated_at: nowIso(),
  }
  store.unshift(created)
  return wait(created)
}

export async function updateProduct(
  appId: string,
  id: string,
  body: Partial<SubscriptionProduct>,
) {
  const item = find(appId, id)
  Object.assign(item, body, {
    id: item.id,
    app_id: item.app_id || appId,
    status: item.status,
    autorenewal_lead_days: 2,
  })
  touch(item)
  return wait(item)
}

export async function publishProduct(appId: string, id: string) {
  const item = find(appId, id)
  const listAt = item.list_at
  const future = listAt && new Date(listAt).getTime() > Date.now()
  item.status = future ? ProductStatus.Scheduled : ProductStatus.Published
  if (!future) item.list_at = nowIso()
  touch(item)
  return wait(item)
}

export async function listNowProduct(appId: string, id: string) {
  const item = find(appId, id)
  item.status = ProductStatus.Published
  item.list_at = nowIso()
  touch(item)
  return wait(item)
}

export async function cancelScheduleProduct(appId: string, id: string) {
  const item = find(appId, id)
  item.status = ProductStatus.Draft
  touch(item)
  return wait(item)
}

export async function unlistProduct(appId: string, id: string) {
  const item = find(appId, id)
  item.status = ProductStatus.Unlisted
  item.unlist_at = null
  touch(item)
  return wait(item)
}

export async function relistProduct(appId: string, id: string) {
  const item = find(appId, id)
  const listAt = item.list_at
  const future = listAt && new Date(listAt).getTime() > Date.now()
  item.status = future ? ProductStatus.Scheduled : ProductStatus.Published
  if (!future) item.list_at = nowIso()
  touch(item)
  return wait(item)
}

export async function deleteProduct(appId: string, id: string) {
  const item = find(appId, id)
  if (item.status !== ProductStatus.Draft) {
    throw new ProductApiError(ProductApiCode.NotDraft, ProductApiCode.NotDraft)
  }
  const index = store.indexOf(item)
  if (index >= 0) store.splice(index, 1)
  return wait(undefined)
}

export async function duplicateProduct(appId: string, id: string) {
  if (ofApp(appId).length >= PRODUCT_LIMIT) {
    throw new ProductApiError(ProductApiCode.Limit, ProductApiCode.Limit)
  }
  const src = find(appId, id)
  const copy: SubscriptionProduct = {
    ...clone(src),
    id: nextId('prod'),
    app_id: appId,
    status: ProductStatus.Draft,
    name: { ...src.name, en: `${src.name.en} copy` },
    list_at: null,
    unlist_at: null,
    created_at: nowIso(),
    updated_at: nowIso(),
  }
  store.unshift(copy)
  return wait(copy)
}
